import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Claim, ClaimStatus } from "../../models/Claim";
import restClient from "../../services/clients/AnonymousRestClient";
import { Link } from "react-router-dom";
import SkeletonCardLoad from "../../components/SkeletonCardLoad";
import { AxiosError } from "axios";
import { AxiosErrorDetail } from "../../models/AxiosErrorDetail";

const EditTravelClaim = () => {

    const { guid } = useParams();

    const [isLoading, setIsLoading]       = useState<boolean>(false);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    const [claim, setClaim] = useState<Claim>();
    const [error, setError] = useState<String>("");

    const navigate = useNavigate();
    
    useEffect(() => {
        /* Fetch data for specific GUID for API */
        restClient.get<Claim>("/api/travelclaim/" + guid)
            .then(response =>
            {   
                /* Check for valid response. Only Claims with Status "Saved" can be edited */
                if (response.status === 200 &&
                    response.data.status === ClaimStatus.Saved)
                {
                    setClaim(response.data);
                }
            })
            .catch(err => {
                // TODO: Proper Error Handler
            })
            .finally(() => {
                setIsLoading(false);
            })
    }, [guid]);

    /**
     * Method that handles form submit. Sends form data via axios instance to api endpoint to update an exisiting 
     * unit cost claim form.
     * @param e Form submit event that needs to be handled.
     * @returns Updated claim.
     */
    async function handleSubmit(e: React.FormEvent<HTMLFormElement>): Promise<Claim | null> {

        /* Prevent default behaviour of HTMLFormElement that would refresh the page */
        e.preventDefault();

        setIsSubmitting(true);
        let updatedClaim: Claim | null = null;

        await restClient.put("/api/travelclaim", claim)
            .then(response => {
                if (response.status === 200)
                {
                    updatedClaim = response.data;
                    navigate("/me/claims/" + updatedClaim?.guid);
                }
            })
            .catch((err: AxiosError) => {
                /* Handle error message | Parse as AxiosErrorDetail */
                if (err.response?.data && (err.response.data as AxiosErrorDetail))
                {
                    setError((err.response?.data as AxiosErrorDetail).errorMessage);
                }
            })
            .finally(() => { setIsSubmitting(false); })

        return updatedClaim;
    }

    /**
     * Returns the maximum amount of nights that can be spend for the current event. 
     * @returns number with maximal nights.
     */
    const getMaxNights = (date1: Date, date2: Date): number => {

        let difference = new Date(date2).getTime() - new Date(date1).getTime();
        let diffrenceDays = difference / (1000 * 3600 * 24);

        // Event Days (+1) and grace day (+1)
        return diffrenceDays > 0 ? diffrenceDays + 1 : 0;
    }

    return (
        <main className="container mb-4">

            <div className="row text-center">
                <div className="col-12">
                    <img src="/brand.png" className="brand" alt="brand" />
                </div>
            </div>

            <div className="row text-center">
                <div className="col-12">
                    <h2>Unit Cost Claim Form</h2>
                </div>
            </div>

            {(error !== "") && (
                <div className="row">
                    <div className="col-12">
                        <div className="alert alert-danger fw-bold">
                            {error}
                        </div>
                    </div>
                </div>
            )}

            <div className="row mb-2">
                <div className="col-12">
                    <Link to="/me/claims" className="btn btn-warning">
                        <i className="bi bi-arrow-left me-2"></i> Back to Overview
                    </Link>
                </div>
            </div>

            {
                isLoading ? <SkeletonCardLoad cardsCount={1} />
                    :
                    <>
                        <div className="card">
                            <div className="card-header">
                                Edit Unit Cost Claim Form
                            </div>

                            <form onSubmit={handleSubmit}>

                                <div className="card-body">

                                    <div className="row">

                                        {claim?.event.accommodationEligible && (
                                            <div className="form-group col-12 text-start">
                                                <label htmlFor="NightSpend" className="fw-bold">Nights spend</label>
                                                <select className="form-select" id="NightSpend" value={claim.calculatedAccommodationCosts?.nights}>
                                                    {
                                                        Array.from(Array(claim.event.maxNights ? claim.event.maxNights + 1 : getMaxNights(claim.event.startDate, claim.event.endDate)).keys()).map((entry, index) =>
                                                        {
                                                            return (
                                                                <option key={index} value={entry}>
                                                                    {entry}
                                                                </option>
                                                            )
                                                        })
                                                    }
                                                </select> 
                                            </div>
                                        )}

                                    </div>

                                </div>

                                <div className="card-body">

                                    <div className="row">
                                        <div className="form-group col-12 col-md-6 text-start mb-1">
                                            <label htmlFor="Firstname" className="fw-bold">Participant Firstname</label>
                                            <input id="Firstname" name="firstname" className="form-control" value={claim?.firstname} />
                                        </div>
                                        <div className="form-group col-12 col-md-6 text-start mb-1">
                                            <label htmlFor="Lastname" className="fw-bold">Participant Lastname</label>
                                            <input id="Lastname" name="lastname" className="form-control" value={claim?.lastname} />
                                        </div>
                                        <div className="form-group col-12 col-md-6 text-start mb-1">
                                            <label htmlFor="Phone" className="fw-bold">Phone</label>
                                            <input id="Phone" name="phone" className="form-control" value={claim?.phone} />
                                        </div>
                                    </div>

                                </div>

                                <div className="card-footer">
                                    {isSubmitting
                                        ? <button type="button" className="btn btn-outline-primary w-100" disabled={true}><img src="/Loading.gif" style={{ width: "32px" }} alt="imgLoading" /></button>
                                        : <button className="btn btn-primary w-100" type="submit">Save Changes</button>
                                    }
                                </div>

                            </form>

                        </div>
                    </>
            }

        </main>
    )

}
export default EditTravelClaim;